/* Groups — /admin/groups
   Sub-tabs: All Groups | New Group
   Group detail modal: members + permissions management */
import { useState, useMemo } from 'react';
import css from '../AdminPage.module.css';
import {
  useAdminGroups,
  useAdminFeatures,
  useAdminUsers,
  useAdminGroupDetail,
  useCreateGroup,
  useUpdateGroup,
  useDeleteGroup,
  useAddGroupMember,
  useRemoveGroupMember,
  useToggleGroupPermission,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import type { UserGroup } from '../../../types/admin';

type SubTab = 'groups' | 'new-group';

export default function Groups({ embedded = false }: { embedded?: boolean }) {
  const { data: groups = [], isLoading } = useAdminGroups();
  const { data: features = [] } = useAdminFeatures();
  const { data: allUsers = [] } = useAdminUsers();
  const { showToast } = useToast();

  const createGroup = useCreateGroup();
  const updateGroup = useUpdateGroup();
  const deleteGroup = useDeleteGroup();
  const addMember = useAddGroupMember();
  const removeMember = useRemoveGroupMember();
  const togglePerm = useToggleGroupPermission();

  const [subTab, setSubTab] = useState<SubTab>('groups');
  const [search, setSearch] = useState('');

  /* new group form */
  const [ngName, setNgName] = useState('');
  const [ngDescription, setNgDescription] = useState('');

  /* detail modal */
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const { data: groupDetail } = useAdminGroupDetail(editingGroupId);

  /* member add form (inside modal) */
  const [memberSearch, setMemberSearch] = useState('');

  /* inline rename */
  const [renamingName, setRenamingName] = useState('');
  const [renamingDesc, setRenamingDesc] = useState('');

  const filtered = useMemo(() => {
    if (!search) return groups;
    const q = search.toLowerCase();
    return groups.filter(
      (g) => g.name.toLowerCase().includes(q) || (g.description || '').toLowerCase().includes(q),
    );
  }, [groups, search]);

  /* group features by category */
  const featuresByCategory = useMemo(() => {
    const map = new Map<string, typeof features>();
    for (const f of features) {
      const cat = f.category || 'General';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(f);
    }
    return map;
  }, [features]);

  const grantedFeatureNames = useMemo(
    () => new Set(groupDetail?.permissions.map((p) => p.feature_name) ?? []),
    [groupDetail],
  );

  const memberUsernames = useMemo(
    () => new Set(groupDetail?.members.map((m) => m.username) ?? []),
    [groupDetail],
  );

  const userSearchResults = useMemo(() => {
    if (!memberSearch.trim()) return [];
    const q = memberSearch.toLowerCase();
    return allUsers
      .filter(
        (u) =>
          u.username.toLowerCase().includes(q) || (u.full_name || '').toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [allUsers, memberSearch]);

  async function handleCreateGroup() {
    if (!ngName.trim()) return;
    try {
      await createGroup.mutateAsync({
        name: ngName.trim(),
        description: ngDescription.trim() || undefined,
      });
      showToast('Group created', 'success');
      setNgName('');
      setNgDescription('');
      setSubTab('groups');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      showToast(msg ?? 'Failed to create group', 'error');
    }
  }

  async function handleDeleteGroup(g: UserGroup) {
    if (!confirm(`Delete group "${g.name}"? Members will lose inherited permissions.`)) return;
    try {
      await deleteGroup.mutateAsync(g.id);
      showToast('Group deleted', 'success');
    } catch {
      showToast('Failed to delete group', 'error');
    }
  }

  function openEdit(g: UserGroup) {
    setEditingGroupId(g.id);
    setRenamingName(g.name);
    setRenamingDesc(g.description ?? '');
    setMemberSearch('');
  }

  async function handleSaveRename() {
    if (!editingGroupId) return;
    try {
      await updateGroup.mutateAsync({
        id: editingGroupId,
        name: renamingName.trim(),
        description: renamingDesc.trim() || undefined,
      });
      showToast('Group updated', 'success');
    } catch {
      showToast('Failed to update group', 'error');
    }
  }

  async function handleAddMember(username: string) {
    if (!editingGroupId) return;
    try {
      await addMember.mutateAsync({ groupId: editingGroupId, username });
      setMemberSearch('');
    } catch {
      showToast('Failed to add member', 'error');
    }
  }

  async function handleRemoveMember(username: string) {
    if (!editingGroupId) return;
    try {
      await removeMember.mutateAsync({ groupId: editingGroupId, username });
    } catch {
      showToast('Failed to remove member', 'error');
    }
  }

  async function handleTogglePerm(featureName: string, currentlyGranted: boolean) {
    if (!editingGroupId) return;
    try {
      await togglePerm.mutateAsync({
        groupId: editingGroupId,
        feature_name: featureName,
        grant: !currentlyGranted,
      });
    } catch {
      showToast('Failed to toggle permission', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <>
      <div className={embedded ? undefined : css.section}>
        {!embedded && (
          <div className={css.sectionHeader}>
            <div>
              <h2 className={css.sectionTitle}>🏷️ User Groups</h2>
              <p className={css.sectionSubtitle}>
                {groups.length} group{groups.length !== 1 ? 's' : ''} — permissions granted to a
                group are inherited by all members
              </p>
            </div>
          </div>
        )}
        {embedded && (
          <p className={css.sectionSubtitle} style={{ marginBottom: 16 }}>
            {groups.length} group{groups.length !== 1 ? 's' : ''} — permissions granted to a group
            are inherited by all members
          </p>
        )}

        {/* Sub-tabs */}
        <div className={css.tabs} style={{ marginBottom: 16 }}>
          <button
            className={subTab === 'groups' ? css.tabActive : css.tab}
            onClick={() => setSubTab('groups')}
          >
            All Groups ({groups.length})
          </button>
          <button
            className={subTab === 'new-group' ? css.tabActive : css.tab}
            onClick={() => setSubTab('new-group')}
          >
            New Group
          </button>
        </div>

        {/* ─── All Groups Tab ─── */}
        {subTab === 'groups' && (
          <>
            <div className={css.toolbar} style={{ marginBottom: 16 }}>
              <div className={css.searchWrapper}>
                <span className={css.searchIcon}>🔍</span>
                <input
                  className={css.searchInput}
                  placeholder="Search groups..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className={css.emptyState}>
                <div className={css.emptyIcon}>🏷️</div>
                <p className={css.emptyMessage}>No groups found</p>
                <button className={css.btnPrimary} onClick={() => setSubTab('new-group')}>
                  Create First Group
                </button>
              </div>
            ) : (
              <table className={css.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Members</th>
                    <th>Permissions</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((g) => (
                    <tr
                      key={g.id}
                      onClick={() => openEdit(g)}
                      style={{ cursor: 'pointer' }}
                      title="Click to manage"
                    >
                      <td style={{ fontWeight: 600 }}>{g.name}</td>
                      <td style={{ color: 'var(--gray-500)', fontSize: 'var(--font-size-sm)' }}>
                        {g.description || '—'}
                      </td>
                      <td>
                        <span className={css.badgeInfo}>{g.member_count ?? 0}</span>
                      </td>
                      <td>
                        <span className={css.badgeNeutral}>{g.permission_count ?? 0}</span>
                      </td>
                      <td>
                        <button
                          className={`${css.btnDanger} ${css.btnSmall}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteGroup(g);
                          }}
                          disabled={deleteGroup.isPending}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* ─── New Group Tab ─── */}
        {subTab === 'new-group' && (
          <div className={css.card} style={{ maxWidth: 520 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>
              Create New Group
            </h3>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Group Name *</label>
              <input
                className={css.formInput}
                value={ngName}
                onChange={(e) => setNgName(e.target.value)}
                placeholder="e.g. Case Handlers"
              />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Description</label>
              <input
                className={css.formInput}
                value={ngDescription}
                onChange={(e) => setNgDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>
            <div style={{ marginTop: 16 }}>
              <button
                className={css.btnPrimary}
                onClick={handleCreateGroup}
                disabled={!ngName.trim() || createGroup.isPending}
              >
                {createGroup.isPending ? 'Creating...' : 'Create Group'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Group Detail Modal ── */}
      {editingGroupId !== null && (
        <div className={css.modalBackdrop} onClick={() => setEditingGroupId(null)}>
          <div
            className={css.modalLg || css.modal}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 760, maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>
                Manage Group: {groupDetail?.name ?? '…'}
                <span
                  style={{
                    fontWeight: 400,
                    fontSize: 'var(--font-size-sm)',
                    marginLeft: 8,
                    color: 'var(--gray-500)',
                  }}
                >
                  {groupDetail?.members.length ?? 0} members ·{' '}
                  {groupDetail?.permissions.length ?? 0} permissions
                </span>
              </h3>
              <button className={css.modalClose} onClick={() => setEditingGroupId(null)}>
                ×
              </button>
            </div>

            <div className={css.modalBody} style={{ overflowY: 'auto', flex: 1 }}>
              {!groupDetail ? (
                <div className={css.spinner}>
                  <div className={css.spinnerDot} />
                  <div className={css.spinnerDot} />
                  <div className={css.spinnerDot} />
                </div>
              ) : (
                <>
                  {/* ── Name / description ── */}
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Name</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        className={css.formInput}
                        value={renamingName}
                        onChange={(e) => setRenamingName(e.target.value)}
                      />
                      <button
                        className={css.btnSecondary}
                        onClick={handleSaveRename}
                        disabled={updateGroup.isPending || !renamingName.trim()}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Description</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        className={css.formInput}
                        value={renamingDesc}
                        onChange={(e) => setRenamingDesc(e.target.value)}
                        placeholder="Optional"
                      />
                    </div>
                  </div>

                  {/* ── Members ── */}
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Members</label>

                    {/* Add member search */}
                    <div style={{ marginBottom: 8, position: 'relative' }}>
                      <input
                        className={css.formInput}
                        placeholder="Search users to add…"
                        value={memberSearch}
                        onChange={(e) => {
                          setMemberSearch(e.target.value);
                        }}
                      />
                      {userSearchResults.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            zIndex: 20,
                            left: 0,
                            right: 0,
                            top: '100%',
                            background: 'var(--surface-primary)',
                            border: '1px solid var(--border-primary)',
                            borderRadius: 'var(--radius)',
                            boxShadow: 'var(--shadow-md)',
                          }}
                        >
                          {userSearchResults.map((u) => (
                            <button
                              key={u.username}
                              style={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                padding: '8px 12px',
                                fontSize: 'var(--font-size-sm)',
                                border: 'none',
                                background: 'transparent',
                                color: memberUsernames.has(u.username)
                                  ? 'var(--gray-400)'
                                  : 'var(--text-primary)',
                                cursor: memberUsernames.has(u.username) ? 'default' : 'pointer',
                              }}
                              onClick={() => {
                                if (memberUsernames.has(u.username)) return;
                                handleAddMember(u.username);
                                setMemberSearch('');
                              }}
                              disabled={memberUsernames.has(u.username)}
                            >
                              <strong>{u.username}</strong>
                              {u.full_name && (
                                <span style={{ marginLeft: 6, color: 'var(--gray-500)' }}>
                                  {u.full_name}
                                </span>
                              )}
                              {memberUsernames.has(u.username) && (
                                <span style={{ marginLeft: 6, color: 'var(--gray-400)' }}>
                                  (already a member)
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Member list */}
                    {groupDetail.members.length === 0 ? (
                      <p
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                          margin: 0,
                        }}
                      >
                        No members yet
                      </p>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {groupDetail.members.map((m) => (
                          <span
                            key={m.username}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '4px 10px',
                              borderRadius: 9999,
                              background: 'var(--primary-50)',
                              border: '1px solid var(--primary-200)',
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--primary-700)',
                            }}
                          >
                            {m.username}
                            <button
                              onClick={() => handleRemoveMember(m.username)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--primary-400)',
                                padding: 0,
                                lineHeight: 1,
                                fontSize: 14,
                              }}
                              title="Remove from group"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ── Permissions ── */}
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>
                      Permissions
                      <span
                        style={{
                          fontWeight: 400,
                          marginLeft: 6,
                          color: 'var(--gray-500)',
                          fontSize: 'var(--font-size-xs)',
                        }}
                      >
                        (inherited by all members)
                      </span>
                    </label>
                    {[...featuresByCategory.entries()].map(([cat, feats]) => (
                      <div key={cat} style={{ marginBottom: 12 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--gray-500)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: 4,
                          }}
                        >
                          {cat}
                        </div>
                        <div style={{ display: 'grid', gap: 4 }}>
                          {feats.map((f) => {
                            const granted = grantedFeatureNames.has(f.feature_name);
                            return (
                              <label
                                key={f.feature_name}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  padding: '6px 10px',
                                  borderRadius: 'var(--radius-sm)',
                                  background: granted
                                    ? 'var(--primary-50, #eff6ff)'
                                    : 'transparent',
                                  border: `1px solid ${granted ? 'var(--primary-200, #bfdbfe)' : 'var(--gray-200)'}`,
                                  cursor: 'pointer',
                                  fontSize: 'var(--font-size-sm)',
                                  transition: 'all 0.15s',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={granted}
                                  onChange={() => handleTogglePerm(f.feature_name, granted)}
                                  disabled={togglePerm.isPending}
                                />
                                <div>
                                  <div style={{ fontWeight: 500 }}>{f.feature_name}</div>
                                  {f.description && (
                                    <div
                                      style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--gray-500)',
                                      }}
                                    >
                                      {f.description}
                                    </div>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setEditingGroupId(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
